import { toMoment } from '@core/utils/time';
import { OrgId } from '@business/entities/organization';
import { BaseBloc } from '@core/utils/bloc';
import { Blocs } from '@business/blocs';
import { DealId } from '@business/entities/deal';
import { resolve } from '@core/utils/ioc';
import { forkJoin, BehaviorSubject } from 'rxjs';
import { LoadingState } from '@core/utils/repository/loading_state';
import { ContactActivityRequest } from '@business/entities/contact';
import { map, tap } from 'rxjs/operators';
import { R } from '@core/utils/r';
import { CalendarEventResponse } from '@business/entities/calendar';

export class DealOverlayBloc extends BaseBloc {
  private readonly dealBloc = resolve(Blocs.deal);
  private readonly dealStageBloc = resolve(Blocs.dealStage);
  private readonly activityBloc = resolve(Blocs.contactActivity);
  private readonly calendarBloc = resolve(Blocs.calendar);
  private readonly taskStatusBloc = resolve(Blocs.taskStatus);
  private readonly userBloc = resolve(Blocs.user);
  private readonly todoBloc = resolve(Blocs.todo);
  private readonly dealStatusBloc = resolve(Blocs.dealStatus);
  private readonly confidenceLevelBloc = resolve(Blocs.dealConfidenceLevel);
  private readonly regionBloc = resolve(Blocs.tmRegion);
  private readonly disqualificationRuleBloc = resolve(Blocs.dealDisqualificationRule);

  constructor(private readonly dealId: DealId, private readonly orgId: OrgId) {
    super();
  }

  deal$ = this.dealBloc.selectDeal(this.dealId);
  stages$ = this.dealStageBloc.stages$;
  readonly estimators$ = this.userBloc.estimators$;
  dealStatuses$ = this.dealStatusBloc.items$;
  confidenceLevels$ = this.confidenceLevelBloc.items$;
  users$ = this.userBloc.staff$;
  events$ = new BehaviorSubject<CalendarEventResponse[]>([]);
  readonly regions$ = this.regionBloc.regions$;
  // Status rules ("disqualification rules") — 'lost'-outcome rules open the Lost Reason popup.
  readonly disqualificationRules$ = this.disqualificationRuleBloc.items$;

  readonly tasks$ = this.todoBloc.tasks$.pipe(
    map(items => R.filter(items, i => R.toString(i.orgId) === R.toString(this.orgId))),
    map(items => R.sortBy(items, item => item.menuOrder)),
  );
  readonly taskStatuses$ = this.taskStatusBloc.statuses$;

  loading = new LoadingState();

  onInit() {
    this.disqualificationRuleBloc.fetchRules().subscribe();
    this.todoBloc.fetchTasks(this.orgId).subscribe();
    this.dealStatusBloc.fetchDealStatuses().subscribe();
    this.confidenceLevelBloc.fetchDealConfidenceLevels().subscribe();
    this.userBloc.fetchEstimators().subscribe();
    this.regionBloc.fetchRegions().subscribe();
    return forkJoin([
      this.dealStageBloc.fetchDealStages(),
      this.dealBloc.fetchDeal(this.dealId),
      this.userBloc.fetchUsers(),
      this.taskStatusBloc.fetchTaskStatuses(),
    ]).pipe(this.loading.run());
  }

  loadEvents = (emails: string) => {
    this.calendarBloc.resetEvents();
    this.calendarBloc
      .events(['0'], toMoment().startOf('day').unix(), toMoment().endOf('day').add(2, 'months').unix(), emails)
      .pipe(tap(items => this.events$.next(items)))
      .subscribe();
  };

  onReload() {
    this.dealBloc.fetchDeal(this.dealId).subscribe();
  }

  createActivity = (request: ContactActivityRequest) => this.activityBloc.createActivity(request);
  readonly activities$ = this.activityBloc.activities$;

  deleteDeal = this.dealBloc.deleteDeal;
  updateDealAction = this.dealBloc.updateDealAction;
  updateDealStatus = this.dealBloc.updateDealStatus;
  updateDealStage = this.dealBloc.updateDealStage;

  updateClosedDate = this.dealBloc.updateClosedDate;

  addEvent = this.calendarBloc.addEvent;
  deleteEvent = this.calendarBloc.deleteEvent;

  createTask = this.todoBloc.createTask;
  updateTask = this.todoBloc.updateTask;
  orderTasks = this.todoBloc.orderTasks;
  deleteTask = this.todoBloc.deleteTask;
  setComplete = this.todoBloc.setComplete;
  searchTask = this.todoBloc.search;

  addProposal = this.dealBloc.addProposal;
  deleteProposal = this.dealBloc.deleteProposal;
}
